import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MessageSquarePlus, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#F2D600", "#0079BF", "#00C2E0", "#61BD4F", "#FF9F1A", "#EB5A46", "#C377E0", "#FF78CB"];

const MESSAGES = {
  invalid: ["This review link isn't valid", "Please double-check the link or ask the project owner for a new one."],
  expired: ["This review link has expired", "Ask the project owner for a new link."],
  revoked: ["This review link is no longer active", "Ask the project owner for a new link."],
};

export default function ReviewerEntry() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[1]);
  const [submitting, setSubmitting] = useState(false);
  const [srvReviewerId, setSrvReviewerId] = useState(null);
  const idKey = `pinlayr_reviewer_local_${token.slice(0, 10)}`;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/reviewer/invite/${token}`);
        const j = await r.json();
        if (!j.ok) { setState({ loading: false, error: j.status || "invalid" }); return; }
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(idKey) || "null"); } catch (_e) { /* ignore */ }
        if (saved) {
          // Same device, returning: reuse their remembered identity.
          setName(saved.name || j.defaultName || "");
          setColor(saved.color || j.defaultColor || COLORS[1]);
        } else {
          // Fresh device: fall back to the identity stored on the link itself,
          // so the member never has to re-enter name/colour on every access.
          setName(j.defaultName || "");
          setColor(j.defaultColor || COLORS[1]);
          setSrvReviewerId(j.reviewerId || null);
        }
        setState({ loading: false, ok: true, projectName: j.projectName });
      } catch (_e) { setState({ loading: false, error: "invalid" }); }
    })();
  }, [token, idKey]);

  const start = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    let reviewerId = null;
    try { reviewerId = (JSON.parse(localStorage.getItem(idKey) || "null") || {}).id || null; } catch (_e) { /* ignore */ }
    if (!reviewerId) reviewerId = srvReviewerId;
    try {
      const r = await fetch(`${API}/api/reviewer/session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name: name.trim(), color, reviewerId }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setState({ loading: false, error: j.detail || "invalid" }); setSubmitting(false); return; }
      const j = await r.json();
      try { localStorage.setItem(idKey, JSON.stringify({ id: j.reviewer.id, name: j.reviewer.name, color: j.reviewer.color })); } catch (_e) { /* ignore */ }
      if (!j.siteUrl) { setState({ loading: false, error: "invalid" }); setSubmitting(false); return; }
      const url = new URL(j.siteUrl);
      url.searchParams.set("plr", j.sessionToken);
      url.searchParams.set("review", "1");
      window.location.href = url.toString();
    } catch (_e) { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0E1016] flex items-center justify-center px-5" data-testid="reviewer-entry">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px 300px at 50% 0%, rgba(109,94,246,0.25), transparent)" }} />
      <div className="relative w-full max-w-[420px] rounded-2xl border border-[#242A38] bg-[#151A24] p-8 shadow-2xl">
        <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ backgroundImage: "linear-gradient(135deg,#4F7CFF,#8B5CF6)" }}>
          <MessageSquarePlus className="h-6 w-6 text-white" />
        </div>

        {state.loading && <p className="mt-6 text-[14px] text-[#98A0B0]">Checking your review link…</p>}

        {state.error && (
          <div data-testid="reviewer-error" data-error={state.error}>
            <div className="mt-5 flex items-center gap-2 text-[#F59E0B]"><AlertTriangle className="h-5 w-5" /><span className="text-[11px] font-semibold uppercase tracking-wider">{state.error}</span></div>
            <h1 className="mt-2 text-[20px] font-semibold text-[#F4F5F8]">{(MESSAGES[state.error] || MESSAGES.invalid)[0]}</h1>
            <p className="mt-2 text-[14px] text-[#98A0B0]">{(MESSAGES[state.error] || MESSAGES.invalid)[1]}</p>
          </div>
        )}

        {state.ok && (
          <div>
            <p className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-[#6E7686]">{state.projectName}</p>
            <h1 className="mt-1 text-[22px] font-semibold tracking-tight text-[#F4F5F8]">Before you start</h1>
            <p className="mt-1.5 text-[14px] text-[#98A0B0]">How should we identify your feedback? No account needed.</p>

            <label className="mt-6 block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Your name</label>
            <input
              autoFocus value={name} onChange={(e) => setName(e.target.value.slice(0, 60))}
              onKeyDown={(e) => e.key === "Enter" && start()}
              placeholder="David"
              className="mt-1.5 w-full rounded-xl border border-[#2A3040] bg-[#0E1016] px-3.5 py-2.5 text-[15px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]"
              data-testid="reviewer-name-input"
            />

            <label className="mt-5 block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Your colour</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} aria-label={c}
                  className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? "border-white" : "border-transparent"}`}
                  style={{ background: c }} data-testid={`reviewer-color-${c.slice(1)}`} />
              ))}
            </div>

            <button
              onClick={start} disabled={submitting || !name.trim()}
              className="mt-7 w-full rounded-xl py-3 text-[15px] font-semibold text-white transition-opacity disabled:opacity-40"
              style={{ backgroundImage: "linear-gradient(135deg,#4F7CFF,#8B5CF6)" }}
              data-testid="reviewer-start-btn"
            >
              {submitting ? "Opening review…" : "Start reviewing"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
